import { useState, useCallback } from 'react';
import { dashboardStorage } from '../services/dashboardStorage';
import { importGrafanaDashboard, exportToGrafana } from '../services/grafanaImporter';

export const useDashboard = () => {
  const [dashboard, setDashboard] = useState({
    uid: null,
    title: 'New Dashboard',
    description: '',
    tags: [],
    time: { from: 'now-6h', to: 'now' },
    timezone: 'browser',
    refresh: '', // Grafana-style: '', '5s', '10s', '30s', '1m', '5m'
    panels: [],
    templating: { list: [] },
    variables: {}
  });

  const [selectedDatasource, setSelectedDatasource] = useState(null);

  // Add new panel
  const addPanel = useCallback((panelConfig = {}) => {
    const newPanel = {
      id: Date.now(),
      type: panelConfig.type || 'timeseries',
      title: panelConfig.title || 'New Panel',
      description: '',
      gridPos: panelConfig.gridPos || { 
        x: 0, 
        y: 0, 
        w: 4, 
        h: 3 
      },
      targets: panelConfig.targets || [{
        refId: 'A',
        expr: '',
        legendFormat: ''
      }],
      datasource: selectedDatasource,
      fieldConfig: {
        defaults: {},
        overrides: []
      },
      options: {},
      data: [],
      loading: false,
      error: null
    };

    setDashboard(prev => ({
      ...prev,
      panels: [...prev.panels, newPanel]
    }));

    return newPanel.id;
  }, [selectedDatasource]);

  // Remove panel
  const removePanel = useCallback((panelId) => {
    setDashboard(prev => ({
      ...prev,
      panels: prev.panels.filter(p => p.id !== panelId)
    }));
  }, []);

  // Duplicate panel (Grafana-style)
  const duplicatePanel = useCallback((panelId) => {
    const panel = dashboard.panels.find(p => p.id === panelId);
    if (!panel) return null;
    const newPanel = {
      ...JSON.parse(JSON.stringify(panel)),
      id: Date.now(),
      title: `${panel.title} (copy)`,
      data: [],
      loading: false,
      error: null,
      gridPos: {
        ...panel.gridPos,
        y: (panel.gridPos?.y ?? 0) + (panel.gridPos?.h ?? 3),
      },
    };
    setDashboard(prev => ({
      ...prev,
      panels: [...prev.panels, newPanel],
    }));
    return newPanel.id;
  }, [dashboard.panels]);

  // Update panel
  const updatePanel = useCallback((panelId, updates) => {
    setDashboard(prev => ({
      ...prev,
      panels: prev.panels.map(p =>
        p.id === panelId ? { ...p, ...updates } : p
      )
    }));
  }, []);

  // Update panel data
  const updatePanelData = useCallback((panelId, data) => {
    setDashboard(prev => ({
      ...prev,
      panels: prev.panels.map(p =>
        p.id === panelId ? { ...p, data, loading: false, error: null } : p
      )
    }));
  }, []);

  // Set panel loading state
  const setPanelLoading = useCallback((panelId, loading) => {
    setDashboard(prev => ({
      ...prev,
      panels: prev.panels.map(p =>
        p.id === panelId ? { ...p, loading } : p
      )
    }));
  }, []);

  // Set panel error
  const setPanelError = useCallback((panelId, error) => {
    setDashboard(prev => ({
      ...prev,
      panels: prev.panels.map(p =>
        p.id === panelId ? { ...p, error, loading: false } : p
      )
    }));
  }, []);

  // Update dashboard metadata
  const updateDashboard = useCallback((updates) => {
    setDashboard(prev => ({ ...prev, ...updates }));
  }, []);

  // Save dashboard to localStorage
  const saveDashboard = useCallback(() => {
    const result = dashboardStorage.save(dashboard);
    return result;
  }, [dashboard]);

  // Load dashboard from localStorage
const loadDashboard = useCallback(async (dashboardName) => {
  const result = await dashboardStorage.load(dashboardName);  
  if (result.success) {
    setDashboard(result.data);  
  }
  return result;
}, []);

  // Import Grafana JSON
  const importDashboard = useCallback((grafanaJSON) => {
    const result = importGrafanaDashboard(grafanaJSON);
    if (result.success) {
      setDashboard(result.dashboard);
    }
    return result;
  }, []);

  // Export to Grafana JSON
  const exportDashboard = useCallback(() => {
    return exportToGrafana(dashboard);
  }, [dashboard]);

  // Update time range
  const updateTimeRange = useCallback((timeRange) => {
    setDashboard(prev => ({
      ...prev,
      time: timeRange
    }));
  }, []);


  // Get variable value by name
  const getVariableValue = useCallback((variableName) => {
    return dashboard.variables[variableName] || '';
  }, [dashboard.variables]);

  // Initialize variables from templating config
  const initializeVariables = useCallback(() => {
    const list = dashboard.templating?.list;
    if (!list?.length) return;
    const initialVars = {};
    list.forEach((varConfig) => {
      initialVars[varConfig.name] = varConfig.current?.value ?? '';
    });
    setDashboard((prev) => ({
      ...prev,
      variables: initialVars,
    }));
  }, [dashboard.templating?.list]);

const updateVariable = useCallback((updatedVariable) => {
  console.log('Updating variable:', updatedVariable);
  setDashboard(prev => {
    const templatingList = prev.templating?.list || [];
    const existingIndex = templatingList.findIndex(v => v.name === updatedVariable.name);

    let newList;
    if (existingIndex >= 0) {
      // Update existing
      newList = [...templatingList];
      newList[existingIndex] = updatedVariable;
    } else {
      // Add new
      newList = [...templatingList, updatedVariable];
    }

    // Set default value
    const defaultValue = updatedVariable.current?.value || updatedVariable.options?.[0] || '';

    return {
      ...prev,
      templating: {
        list: newList
      },
      variables: {
        ...prev.variables,
        [updatedVariable.name]: defaultValue
      }
    };
  });
}, []);

const deleteVariable = useCallback((variableName) => {
  setDashboard(prev => ({
    ...prev,
    templating: {
      ...prev.templating,
      list: prev.templating.list.filter(v => v.name !== variableName)
    },
    variables: {
      ...prev.variables,
      [variableName]: undefined
    }
  }));
}, []);

  return {
    dashboard,
    addPanel,
    removePanel,
    duplicatePanel,
    updatePanel,
    updatePanelData,
    setPanelLoading,
    setPanelError,
    updateDashboard,
    saveDashboard,
    loadDashboard,
    importDashboard,
    exportDashboard,
    updateTimeRange,
    selectedDatasource,
    setSelectedDatasource,
    updateVariable,
    getVariableValue,
    initializeVariables,
    deleteVariable,
  };
};